import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bot, Check, Loader2, Send, X } from 'lucide-react';
import api, { extractError } from '../../lib/api';
import { useAuth } from '../../lib/auth';

export type AiBotPage = 'data-fields' | 'apis' | 'questionnaires';

interface AiBotProposal {
  id: string;
  toolName: string;
  summary: string;
  page: AiBotPage;
  payload: Record<string, any>;
  expiresAt: string;
}

interface ChatEntry {
  role: 'user' | 'assistant';
  content: string;
}

/** Klare Rückmeldung nach einem ausgeführten Vorschlag. */
function createdMessage(toolName: string | undefined, name: string | undefined): string {
  const label =
    toolName === 'create_api_endpoint'
      ? 'API-Endpunkt'
      : toolName === 'create_cluster'
        ? 'Cluster'
        : toolName === 'create_questionnaire'
          ? 'Fragebogen'
          : 'Datenfeld';
  const subject = name ? `${label} „${name}“` : 'Der Datensatz';
  return `✅ ${subject} wurde angelegt. Du musst nichts mehr absenden – der Eintrag ist bereits gespeichert.`;
}

interface AiBotConfirmResult {
  toolName?: string;
  payload?: Record<string, any>;
  result?: { id?: string; name?: string } | null;
}

interface AiBotWidgetProps {
  /** Datenfelder- oder API-Seite. */
  page: AiBotPage;
  /** Nur true, wenn das Anlege-Formular offen ist (nicht im Bearbeiten-Modus). */
  active: boolean;
  /**
   * Wird nach bestätigter Anlage aufgerufen. Die Seite schließt damit das
   * Anlege-Formular und aktualisiert die Liste; ein vorbefülltes Formular,
   * das erneut abgeschickt werden könnte, gibt es bewusst nicht mehr.
   */
  onCreated?: (result: AiBotConfirmResult) => void;
}

/**
 * Schwebendes KI-Bot-Icon inklusive Chat-Panel.
 *
 * Sichtbar wenn:
 *  (a) Datenfelder- oder API-Seite,
 *  (b) Anlege-Formular offen oder das Chat-Panel noch geöffnet (damit die
 *      Rückmeldung nach einer bestätigten Anlage sichtbar bleibt),
 *  (c) /ai-bot/status enabled=true meldet,
 *  (d) Rolle Admin (ADMIN_*) oder Editor (USER_EDITOR).
 */
export default function AiBotWidget({ page, active, onCreated }: AiBotWidgetProps) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatEntry[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [proposal, setProposal] = useState<AiBotProposal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const canUse =
    Boolean(user?.role?.startsWith('ADMIN_')) || user?.role === 'USER_EDITOR';

  const { data: status } = useQuery<{ enabled: boolean }>({
    queryKey: ['ai-bot-status'],
    queryFn: async () => (await api.get('/ai-bot/status')).data,
    enabled: active && canUse,
    staleTime: 30_000,
    retry: false,
  });

  // Nach einer bestätigten Anlage schließt die Seite das Formular (active=false).
  // Ist das Chat-Panel dann noch offen, bleibt es sichtbar, damit die
  // Rückmeldung „wurde bereits angelegt“ gelesen werden kann.
  const visible = canUse && status?.enabled === true && (active || isOpen);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, proposal, sending]);

  if (!visible) return null;

  const appendAssistant = (content: string) => {
    setMessages((prev) => [...prev, { role: 'assistant', content }]);
  };

  async function sendMessage(e: React.FormEvent) {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    setError(null);
    const history = messages.slice(-10);
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setInput('');
    setSending(true);
    setProposal(null);

    try {
      const { data } = await api.post('/ai-bot/chat', {
        message: text,
        context: { page, mode: 'create' },
        history,
      });

      if (data?.reply) {
        appendAssistant(String(data.reply));
      }
      if (data?.proposal) {
        setProposal(data.proposal as AiBotProposal);
      }
    } catch (err) {
      const message = extractError(err).message;
      setError(message);
      appendAssistant(`Das hat leider nicht funktioniert: ${message}`);
    } finally {
      setSending(false);
    }
  }

  async function confirmProposal() {
    if (!proposal || confirming) return;
    // Referenz festhalten: setProposal(null) weiter unten darf den laufenden
    // Aufruf nicht beeinflussen.
    const confirmed = proposal;
    setConfirming(true);
    setError(null);

    try {
      const { data } = await api.post('/ai-bot/confirm', { proposalId: confirmed.id });
      const createdName =
        data?.result?.name ?? data?.payload?.name ?? confirmed.payload?.name;

      // Der Datensatz ist gespeichert. Vorschlag und Formular werden nicht
      // erneut befüllt, damit die Anlage nicht versehentlich doppelt abgeschickt wird.
      setProposal(null);
      appendAssistant(createdMessage(data?.toolName, createdName));
      onCreated?.(data as AiBotConfirmResult);
    } catch (err) {
      const { message, status } = extractError(err);
      // 409/410 bzw. „bereits …“: Der Vorschlag ist verbraucht (z. B. weil die
      // erste Bestätigung serverseitig schon durchlief). Ein erneuter Versuch
      // ist sinnlos – der Bestätigen-Button verschwindet.
      if (status === 409 || status === 410 || /bereits (ausgeführt|bearbeitet)/i.test(message)) {
        setProposal(null);
        appendAssistant(`Dieser Vorschlag kann nicht erneut ausgeführt werden: ${message}`);
      } else {
        setError(message);
      }
    } finally {
      setConfirming(false);
    }
  }

  async function discardProposal() {
    if (!proposal) return;
    const discarded = proposal;
    setProposal(null);
    setError(null);
    try {
      await api.post('/ai-bot/discard', { proposalId: discarded.id });
      appendAssistant('Vorschlag verworfen. Ich habe nichts gespeichert.');
    } catch {
      appendAssistant('Vorschlag verworfen.');
    }
  }

  return (
    <>
      {isOpen && (
        <div className="fixed bottom-20 right-4 z-[60] flex max-h-[70vh] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl sm:w-[22rem]">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <Bot size={18} className="text-primary" />
              <div>
                <p className="text-sm font-semibold text-gray-900">KI-Assistent</p>
                <p className="text-xs text-gray-500">
                  {page === 'apis'
                    ? 'Hilfe beim Anlegen von API-Endpunkten'
                    : page === 'questionnaires'
                      ? 'Hilfe beim Anlegen von Fragebögen'
                      : 'Hilfe beim Anlegen von Datenfeldern'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Chat schließen"
            >
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {messages.length === 0 && (
              <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
                Beschreibe{' '}
                {page === 'apis'
                  ? 'den neuen API-Endpunkt'
                  : page === 'questionnaires'
                    ? 'den neuen Fragebogen'
                    : 'das neue Datenfeld'}.
                Ich prüfe vorhandene Einträge und schlage die Anlage vor – gespeichert wird erst nach
                deiner Bestätigung.
              </div>
            )}

            {messages.map((entry, index) => (
              <div
                key={`${entry.role}-${index}`}
                className={entry.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
              >
                <div
                  className={
                    entry.role === 'user'
                      ? 'max-w-[85%] rounded-lg bg-primary px-3 py-2 text-sm text-white'
                      : 'max-w-[85%] whitespace-pre-wrap rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-800'
                  }
                >
                  {entry.content}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 size={14} className="animate-spin" />
                Der Bot prüft die Angaben…
              </div>
            )}

            {proposal && (
              <div className="rounded-lg border border-primary/30 bg-primary-50 p-3">
                <div className="flex items-start gap-2">
                  <Check size={16} className="mt-0.5 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{proposal.summary}</p>
                    <p className="mt-1 text-xs text-gray-600">
                      Vorschlag – wird erst nach Bestätigung gespeichert.
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={confirmProposal}
                    disabled={confirming}
                    className="btn-primary px-3 py-1.5 text-xs"
                  >
                    {confirming ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    Bestätigen
                  </button>
                  <button
                    type="button"
                    onClick={discardProposal}
                    disabled={confirming}
                    className="btn-secondary px-3 py-1.5 text-xs"
                  >
                    Verwerfen
                  </button>
                </div>
              </div>
            )}

            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>

          <form onSubmit={sendMessage} className="flex items-center gap-2 border-t border-gray-200 p-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="input-field"
              placeholder="z.B. Lege ein Pflichtfeld Telefonnummer an"
              disabled={sending}
              maxLength={4000}
            />
            <button
              type="submit"
              className="btn-primary shrink-0 p-2"
              disabled={sending || !input.trim()}
              aria-label="Nachricht senden"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="fixed bottom-4 right-4 z-[60] flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white shadow-lg transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label={isOpen ? 'KI-Assistent schließen' : 'KI-Assistent öffnen'}
        title="KI-Assistent"
      >
        <Bot size={22} />
      </button>
    </>
  );
}
